import dayjs from 'dayjs';
import React, {
  Dispatch,
  SetStateAction,
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { useTabManager } from '../../app-v2/context/tab-manager-context';
import { useFolderTagsByLimit, useSubSystemsById } from '../hooks/folders';

import {
  LimitConfig,
  LimitMatchType,
  MatchType,
  SubSystem,
  SystemsWithStates,
  Tag,
  ViewType,
} from '@controlrooms/models';

interface ContextProps {
  folder: SubSystem | undefined;
  tag: Tag | undefined;
  filterString: string;
  limitMatchType: LimitMatchType;
  stateTags: Tag[];
  defaultLimit: LimitConfig;
  updateTagSelection: (
    folder: SubSystem,
    tag: Tag,
    systemsWithStates?: SystemsWithStates[],
  ) => void;
  setEditableLimit: Dispatch<SetStateAction<LimitConfig | undefined>>;
  setFilterString: Dispatch<SetStateAction<string>>;
  setLimitMatchType: Dispatch<SetStateAction<LimitMatchType>>;
  editableLimit: LimitConfig | undefined;
  matchType: MatchType;
  setMatchType: Dispatch<SetStateAction<MatchType>>;
}

const dateFormat = 'YYYY-MM-DDTHH:mm:ss';
const defaultEndDate = '2050-12-30T23:59:59';

const defaultLimit: LimitConfig = {
  limit_id: 0,
  tag_name: '',
  tag_display_name: '',
  start_time: dayjs().format(dateFormat),
  end_time: defaultEndDate,
  low_low_limit: null,
  low_limit: null,
  high_limit: null,
  high_high_limit: null,
  criticality: 'Low',
  limit_type: 'Numeric',
  is_active: true,
  states: [],
};

const defaultState: ContextProps = {
  tag: undefined,
  defaultLimit,
  stateTags: [],
  limitMatchType: LimitMatchType.WITH_LIMITS,
  setLimitMatchType: () => undefined,
  matchType: MatchType.ANY,
  setMatchType: () => undefined,
  filterString: '',
  setFilterString: () => undefined,
  folder: undefined,
  updateTagSelection: () => undefined,
  setEditableLimit: () => undefined,
  editableLimit: undefined,
};

export const LimitContext = createContext<ContextProps>(defaultState);

const LimitContextProvider: React.FC = ({ children }) => {
  const { data: folderTags } = useFolderTagsByLimit();
  const { data: systemData } = useSubSystemsById();
  const { activeViewState, updateActiveTabViewState } = useTabManager();
  const limitManagerView = activeViewState?.view[ViewType.LIMIT_MANAGER];
  const hasRestoredFromViewState = useRef(false);

  const [tag, setTag] = useState<Tag | undefined>();
  const [stateTags, setStateTags] = useState<Tag[]>([]);
  const [filterString, setFilterString] = useState('');
  const [folder, setFolder] = useState<SubSystem>();
  const [matchType, setMatchType] = useState<MatchType>(MatchType.ANY);
  const [limitMatchType, setLimitMatchType] = useState<LimitMatchType>(LimitMatchType.WITH_LIMITS);
  const [editableLimit, setEditableLimit] = useState<LimitConfig | undefined>();

  // Restore folder and tag from saved view state
  useEffect(() => {
    if (!limitManagerView || !systemData || !folderTags || hasRestoredFromViewState.current) return;

    const folderId = limitManagerView.folder;
    const tagName = limitManagerView.tag;
    if (folderId == null || !tagName) return;

    const systemObj = systemData[folderId];
    const matchingTag = (folderTags[folderId] || []).find((t: Tag) => t.name === tagName);
    if (!systemObj || !matchingTag) return;

    hasRestoredFromViewState.current = true;
    setFolder(systemObj as SubSystem);
    setTag(matchingTag);
  }, [limitManagerView, systemData, folderTags]);

  // Persist folder and tag selection to view state, marking tab dirty on change
  useEffect(() => {
    if (!hasRestoredFromViewState.current) return;
    if (!activeViewState || !updateActiveTabViewState) return;

    const selectionChanged =
      folder?.folder !== limitManagerView?.folder || tag?.name !== limitManagerView?.tag;

    updateActiveTabViewState({
      ...activeViewState,
      ...(selectionChanged ? { isDirty: true } : {}),
      view: {
        ...activeViewState.view,
        [ViewType.LIMIT_MANAGER]: {
          ...activeViewState.view[ViewType.LIMIT_MANAGER],
          folder: folder?.folder,
          tag: tag?.name,
        },
      },
    }).catch(() => undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [folder?.folder, tag?.name]);

  const updateTagSelection = useCallback(
    (selectedFolder: SubSystem, selectedTag: Tag, systemsWithStates?: SystemsWithStates[]) => {
      setFolder(selectedFolder);
      setEditableLimit(undefined);

      if (selectedFolder.tags.some((t: Tag) => t.name === selectedTag.name)) {
        setTag(selectedTag);
      }

      const systemStateTags =
        systemsWithStates?.find(
          (sys: SystemsWithStates) => sys.infra_node_id === selectedFolder.folder,
        )?.state_tag_names ?? [];

      const matchedStateTags = (folderTags[selectedFolder.folder] ?? []).filter((t: Tag) =>
        systemStateTags.includes(t.name),
      );
      setStateTags(matchedStateTags);
    },
    [folderTags],
  );

  const appState = useMemo(
    () => ({
      folder,
      tag,
      defaultLimit,
      stateTags,
      filterString,
      setFilterString,
      updateTagSelection,
      editableLimit,
      setEditableLimit,
      limitMatchType,
      setLimitMatchType,
      matchType,
      setMatchType,
    }),
    [
      editableLimit,
      filterString,
      folder,
      limitMatchType,
      matchType,
      stateTags,
      tag,
      updateTagSelection,
    ],
  );

  return <LimitContext.Provider value={appState}>{children}</LimitContext.Provider>;
};

export default LimitContextProvider;
